import React from 'react';
import { cloneDeep, flatten, isEqualWith } from 'lodash';
import { parseToRgb } from 'polished';

export const mapOverTree = (from, cb, parent) => {
	const to = from.map((fromItem, index) => {
		const toItem = cb(fromItem, index, parent);

		if (toItem.children) {
			toItem.children = flatten(mapOverTree(toItem.children, cb, toItem));
		}

		return toItem;
	});

	return to;
};

export const getFilteredTree = (from, cb, filterChildrenFirst = false) => {
	let to = filterChildrenFirst ? [...from] : from.filter(cb);

	to = to.map(fromItem => {
		return {
			...fromItem,
			children: getFilteredTree(fromItem.children, cb, filterChildrenFirst),
		};
	});

	if (filterChildrenFirst) {
		to = to.filter(cb);
	}

	return to;
};

export const iterateOverTree = (from, cb) => {
	from.forEach((fromItem, index) => {
		cb(fromItem, index);

		if (fromItem.children) {
			iterateOverTree(fromItem.children, cb);
		}
	});
};

export const expandAll = (from, expand = true) => {
	const fromCopy = cloneDeep(from);

	fromCopy.forEach(fromItem => {
		fromItem.expanded = expand;

		// L0 is allways expanded
		if (fromItem.level === 0) {
			fromItem.expanded = true;
		}

		if (fromItem.children) {
			fromItem.children = expandAll(fromItem.children, expand);
		}
	});

	return fromCopy;
};

export const lightenColor = (color, ratio) => {
	const { red, green, blue } = parseToRgb(color);

	// ratio has to be in range (0..1)
	ratio = ratio % 1;

	const lightenRed = red * ratio + 255 * (1 - ratio);
	const lightenGreen = green * ratio + 255 * (1 - ratio);
	const lightenBlue = blue * ratio + 255 * (1 - ratio);

	const result = `rgb(${lightenRed}, ${lightenGreen}, ${lightenBlue})`;

	return result;
};

export const getNodeKey = ({ node, treeIndex }) => node.id + '.' + treeIndex;

// for all items - if all my children are mapped - I'm mapped also
const syncMappedFlag = from => {
	const to = from.map(fromItem => {
		const toItem = { ...fromItem };

		if (fromItem.children && fromItem.children.length > 0) {
			toItem.children = syncMappedFlag(fromItem.children);

			toItem.isMapped = toItem.children.every(item => item.isMapped);
			// L3,2,1,0 - if no children - consider them as mapped
		} else if (toItem.level <= 3) {
			toItem.isMapped = true;
		}

		return toItem;
	});

	return to;
};

export const syncValues = from => {
	const to = from.map(fromItem => {
		const toItem = { ...fromItem };

		if (toItem.children && toItem.children.length > 0) {
			toItem.children = syncValues(toItem.children);

			toItem.value = toItem.children
				.map(item => item.value)
				.reduce((acc, x) => x + acc, 0);

			toItem.chartData = getItemChartData(toItem.children);
		} else if (toItem.level < 4) {
			toItem.value = 0;
			toItem.chartData = undefined;
		}

		return toItem;
	});

	return to;
};

// items are added from 'from' tree into 'to' tree - so 'from' tree needs to
// have added items marked as such
export const markMappedItems = (from, to) => {
	const ids = [];

	iterateOverTree(to, item => {
		if (item.level === 4 || item.level === 5) {
			ids.push(item.id);
		}
	});
	let result = mapOverTree(from, item => ({
		...item,
		isMapped: ids.includes(item.id),
	}));

	result = syncMappedFlag(result);

	return result;
};

export const removeMappedItems = from => {
	return getFilteredTree(from, item => item.level === 0 || !item.isMapped);
};

export const getSortedTree = (from, field = 'name', direction = 1) => {
	const fromCopy = cloneDeep(from);

	direction = +direction;
	direction = isNaN(direction) ? 1 : direction;

	fromCopy.sort((a, b) => {
		let aValue = a[field];
		let bValue = b[field];

		if (typeof aValue === 'string' || typeof bValue === 'string') {
			aValue = aValue.toUpperCase();
			bValue = bValue.toUpperCase();
		}

		return aValue > bValue ? direction : -1 * direction;
	});

	fromCopy.forEach(fromItem => {
		fromItem.children = getSortedTree(fromItem.children, field, direction);
	});

	return fromCopy;
};

export const getNormalizedInsertedTreeNode = (insertedNode, parentNode) => {
	// removed mapped items from inserted root node
	insertedNode.children = getFilteredTree(
		insertedNode.children,
		item => !item.isMapped
	);

	// remove company level if inserted - not depending on isertion level
	if (insertedNode.level === 0) {
		insertedNode = insertedNode.children.map(insertedNode =>
			doTreeNodeNormalization(insertedNode, parentNode)
		);
	} else {
		insertedNode = doTreeNodeNormalization(insertedNode, parentNode);
	}

	return insertedNode;
};

const doTreeNodeNormalization = (insertedNode, parentNode) => {
	const parentNodeLevel = parentNode ? parentNode.level : 0;
	const insertedNodeLevel = insertedNode.level;

	// parentNodeLevel: 0 1 2 3 4
	// insertedNodeLevel: 1 2 3 4 (5)

	// 1 -> 0 // keep it
	// 2,3 -> 0 // with no L4 children - insert it without adding missing level
	// 2 -> 0 // add missing level - L1=L2, L2=L2+' (copy)' L3=L3, L4=L4
	// 3 -> 0 // add missing level - L1=L3, L2=L3+' (copy 1)' L3=L3+' (copy 2)', L4=L4
	// 4 -> 0 // add missing level - L1=L4+' (copy 1), L2=L3+' (copy 2)' L3=L3+' (copy 3)', L4=L4
	if (parentNodeLevel === 0) {
		const l4ItemsCount = getLeafIdsFromNode(insertedNode).length;

		if (
			l4ItemsCount === 0 &&
			(insertedNodeLevel === 2 || insertedNodeLevel === 3)
		) {
			insertedNode = {
				...insertedNode,
				level: 1,
				children: insertedNode.children.map(item => ({ ...item, level: 2 })),
			};
		} else if (insertedNodeLevel === 2 && l4ItemsCount > 0) {
			insertedNode = {
				...insertedNode,
				level: 1,
				children: [
					{
						...insertedNode,
						name: `${insertedNode.name} (Copy)`,
					},
				],
			};
		} else if (insertedNodeLevel === 3 && l4ItemsCount > 0) {
			insertedNode = {
				...insertedNode,
				level: 1,
				children: [
					{
						...insertedNode,
						level: 2,
						name: `${insertedNode.name} (Copy 1)`,
						children: [
							{
								...insertedNode,
								name: `${insertedNode.name} (Copy 2)`,
							},
						],
					},
				],
			};
		} else if (insertedNodeLevel >= 4) {
			insertedNode = {
				...insertedNode,
				level: 1,
				name: `${insertedNode.name} (Copy 1)`,
				children: [
					{
						...insertedNode,
						level: 2,
						name: `${insertedNode.name} (Copy 2)`,
						children: [
							{
								...insertedNode,
								level: 3,
								name: `${insertedNode.name} (Copy 3)`,
								children: [
									{
										...insertedNode,
										level: 4,
									},
								],
							},
						],
					},
				],
			};
		}
	}
	// 1 -> 1 // L1=L2, L2=L3, L3=L4, childless L4=L5 or get its L5 children
	// 2 -> 1 // keep it
	// 3 -> 1 // with no L4 children - insert it without adding missing level
	// 3 -> 1 // add missing level - L2=L3 L3=L3+' (copy)' , L4=L4
	// 4 -> 1 // add missing levels - L2=L4+' (copy 1)'  L3=L4+' (copy 2)' , L4=L4
	if (parentNodeLevel === 1) {
		if (insertedNodeLevel === 1) {
			insertedNode = { ...insertedNode, level: 2 };
			insertedNode.children = insertedNode.children.map(l2Node => {
				l2Node = { ...l2Node, level: 3 };
				l2Node.children = l2Node.children.map(item => {
					const toItem = {
						...item,
						level: 4,
						children: item.children.map(
							({ chartData: _0, ...item }) =>
								item.children.length > 0
									? item.children
									: {
											...item,
											level: 5,
									  }
						),
					};

					toItem.children = flatten(toItem.children);

					return toItem;
				});
				l2Node.children = l2Node.children.filter(
					item => item.children.length > 0
				);

				return l2Node;
			});
		} else if (insertedNodeLevel === 3 && insertedNode.children.length === 0) {
			insertedNode = {
				...insertedNode,
				level: 2,
			};
		} else if (insertedNodeLevel === 3) {
			insertedNode = {
				...insertedNode,
				level: 2,
				children: [
					{
						...insertedNode,
						level: 3,
						name: `${insertedNode.name} (Copy)`,
					},
				],
			};
		} else if (insertedNodeLevel >= 4) {
			insertedNode = {
				...insertedNode,
				level: 2,
				name: `${insertedNode.name} (Copy 1)`,
				children: [
					{
						...insertedNode,
						level: 3,
						name: `${insertedNode.name} (Copy 2)`,
						children: [
							{
								...insertedNode,
								level: 4,
							},
						],
					},
				],
			};
		}
	}

	// 1 -> 2 // L1=L3, L2 removed, L3=L4, L4=L5
	// 2 -> 2 // L2=L3, L3=L4, L4=L5
	// 3 -> 2 // kepp it
	// 4 -> 2 // add missing level - L3=L4, L4=L4+` (copy)`
	if (parentNodeLevel === 2) {
		if (insertedNodeLevel <= 2) {
			if (insertedNodeLevel === 1) {
				// remove l2
				insertedNode.children = insertedNode.children.map(
					item => item.children
				);
				insertedNode.children = flatten(insertedNode.children);
			}

			insertedNode.level = 3;
			insertedNode.children = insertedNode.children.map(item => {
				const toItem = {
					...item,
					level: 4,
					children: item.children.map(
						({ chartData: _0, ...item }) =>
							item.children.length > 0
								? item.children
								: {
										...item,
										level: 5,
								  }
					),
				};

				toItem.children = flatten(toItem.children);

				return toItem;
			});
			insertedNode.children = insertedNode.children.filter(
				item => item.children.length > 0
			);
		} else if (insertedNodeLevel >= 4) {
			insertedNode = {
				...insertedNode,
				level: 3,
				name: `${insertedNode.name} (Copy)`,
				children: [
					{
						...insertedNode,
						level: 4,
					},
				],
			};
		}
	}
	// 1 -> 3 // L1=L4, L2, L3 removed, L4=L5
	// 2 -> 3 // L2=L4, L3 removed, L4=L5
	// 3 -> 3 // L3=L4, L4=L5
	// 4 -> 3 // keep it
	if (parentNodeLevel === 3) {
		if (insertedNodeLevel !== 4) {
			insertedNode.level = 4;
			insertedNode.children = getAllLeaves(insertedNode.children);
			insertedNode.children = insertedNode.children.map(
				({ chartData: _0, ...item }) => ({
					...item,
					level: 5,
				})
			);
			delete insertedNode.chartData;
			if (insertedNode.children === 0) {
				insertedNode = [];
			}
		}
	}

	// 1 -> 4 // L4=L5, remove others, current L4 becomes L5
	// 2 -> 4 // L4=L5, remove others, current L4 becomes L5
	// 3 -> 4 // L4=L5, remove others, current L4 becomes L5
	// 4 -> 4 // L4=L5
	if (parentNodeLevel === 4) {
		if (insertedNodeLevel === 4) {
			insertedNode.level = 5;
			delete insertedNode.chartData;

			// flatten its L5
			if (insertedNode.children.length > 0) {
				insertedNode = insertedNode.children;
			}
		} else {
			insertedNode = getAllLeaves(insertedNode.children);
			// convert all to L5's
			insertedNode = insertedNode.map(({ chartData: _0, ...item }) => ({
				...item,
				level: 5,
			}));
		}

		if (parentNode.children.length === 1) {
			const parentNodeCopy = cloneDeep(parentNode);
			parentNodeCopy.level = 5;
			parentNodeCopy.children = [];
			delete parentNodeCopy.chartData;

			insertedNode = Array.isArray(insertedNode)
				? insertedNode
				: [insertedNode];
			insertedNode = [parentNodeCopy, ...insertedNode];
			parentNode.id = parentNode.id + '_virtual';
		}
	}

	return insertedNode;
};

export const getAllLeaves = from => {
	const leaves = [];

	iterateOverTree(from, fromItem => {
		if (
			(fromItem.level === 4 && fromItem.children.length === 0) ||
			fromItem.level === 5
		) {
			leaves.push(fromItem);
		}
	});

	return leaves;
};

export const getTreeWrappedInRootNode = children => {
	const rootItem = {
		name: 'Project',
		id: PROECT_TREE_ROOT_ELEMENT_ID,
		expanded: true,
		level: 0,
		children,
	};

	return rootItem;
};

export const insertIntoRootNode = (node, from) => {
	let rootItem = from[0];

	node = Array.isArray(node) ? node : [node];

	if (rootItem && rootItem.id === PROECT_TREE_ROOT_ELEMENT_ID) {
		rootItem.children = [...node, ...rootItem.children];
	} else {
		rootItem = getTreeWrappedInRootNode(node);
	}

	return [rootItem];
};

export const getItemChartData = from => {
	const chartDataMap = {};

	from.forEach(fromItem => {
		const chartDataItems = fromItem.chartData
			? cloneDeep(fromItem.chartData)
			: fromItem.value
				? [
						{
							name: fromItem.companyName,
							value: fromItem.value,
							color: fromItem.companyColor,
						},
				  ]
				: [];

		chartDataItems.forEach(item => {
			if (chartDataMap[item.name]) {
				chartDataMap[item.name].value += item.value;
			} else {
				chartDataMap[item.name] = item;
			}
		});
	});

	return Object.values(chartDataMap);
};

const PROECT_TREE_ROOT_ELEMENT_ID = 'project';
export const getNormalizedProjectTreeData = from => {
	if (!from || from.length === 0) {
		return from;
	}

	let rootNode = from[0];

	// reassign ids
	rootNode.children = mapOverTree(
		rootNode.children,
		(item, index, parent) => {
			if (item.level === 4 && item.children.length === 1) {
				item = { ...item.children[0], level: 4 };
			}

			if (
				(item.level === 4 && item.children.length === 0) ||
				item.level === 5
			) {
				return item;
			}

			return {
				...item,
				id:
					parent.id +
					'/' +
					item.name +
					'/' +
					index +
					'/' +
					Math.random() * 1000,
			};
		},
		rootNode
	);

	const result = syncValues([rootNode]);

	return result;
};

export const getTreesChangeData = (from, to, parentNode) => {
	// if list has increased - found parent node
	if (to.length > from.length) {
		// search for index
		for (let index = 0; index < to.length; index += 1) {
			const toItem = to[index];
			const fromItem = from.find(fromItem => fromItem.id === toItem.id);

			if (!fromItem) {
				return {
					parentNode,
					index,
				};
			}
		}
	} else {
		// sarch for diff by comparing children lists
		for (let index = 0; index < to.length; index += 1) {
			const toItem = to[index];
			const fromItem = from.find(fromItem => fromItem.id === toItem.id);

			const diff = getTreesChangeData(
				fromItem.children,
				toItem.children,
				toItem
			);

			if (diff) {
				return diff;
			}
		}
	}

	return null;
};

export const generateMockTree = (level = 1, maxLevel = 5, index = 0) => {
	const maxChildCount = 3;

	const tree = generateMockTreeItem(level, index);

	if (level !== maxLevel) {
		tree.children = Array(maxChildCount)
			.fill(0)
			.map((_0, index) => {
				return generateMockTree(level + 1, maxLevel, index);
			});
	}

	return tree;
};

export const generateMockTreeItem = (level, index = 0) => ({
	level,
	name: `Item${index} on L${level}`,
	id: `Item${index} on L${level}`,
	value: 10 ** 3,
	children: [],
	companyName: 'Test company',
	companyColor: '#FF0000',
});

export const createNewCategory = (overrides = {}) => {
	return {
		name: 'New category',
		id: 'new category' + Math.random() * 1000,
		level: 1,
		children: [],
		...overrides,
	};
};

export const addNewL1Category = from => {
	const newNode = createNewCategory();

	return insertIntoRootNode(newNode, from);
};

export const addNewCategoryToParent = (from, parent) => {
	let to = cloneDeep(from);

	to = mapOverTree(to, item => {
		if (item.id === parent.id) {
			const newCategory = createNewCategory({
				level: parent.level + 1,
			});

			return {
				...item,
				expanded: true,
				children: [newCategory, ...item.children],
			};
		}

		return item;
	});

	return to;
};

export const addNewCategoryAsSybling = (from, node) => {
	let to = cloneDeep(from);

	to = mapOverTree(to, item => {
		if (item.children.length > 0) {
			const insertIndex = item.children.findIndex(item => item.id === node.id);

			if (insertIndex !== -1) {
				const newCategory = createNewCategory({
					level: node.level,
				});

				item.children.splice(insertIndex, 0, newCategory);
			}
		}

		return item;
	});

	return to;
};

export const TREE_MENU_VALUES = {
	mergeWithAbove: 'merge_with_above',
	mergeWithBelow: 'merge_with_below',
	createSibling: 'create_sibling',
	createChild: 'create_child',
	find: 'find',
	remove: 'remove',
};

export const COMPANY_FIND_MENU_OPTIONS = [
	{
		value: TREE_MENU_VALUES.find,
		label: 'Find in project taxonomy',
	},
	{
		value: TREE_MENU_VALUES.remove,
		label: 'Remove from project taxonomy',
	},
];

export const PROJECT_FIND_MENU_OPTIONS = [
	{
		value: TREE_MENU_VALUES.find,
		label: 'Find in company taxonomy',
	},
	{
		value: TREE_MENU_VALUES.remove,
		label: 'Remove from project taxonomy',
	},
];

export const CREATE_CHILD_OPTION = {
	value: TREE_MENU_VALUES.createChild,
	label: 'Create new child item',
};

export const CREATE_SIBLING_OPTION = {
	value: TREE_MENU_VALUES.createSibling,
	label: 'Create sibling item',
};

export const CREATE_MENU_OPTIONS = [CREATE_CHILD_OPTION, CREATE_SIBLING_OPTION];

export const MERGE_WITH_ABOVE_OPTION = {
	value: TREE_MENU_VALUES.mergeWithAbove,
	label: 'Merge with item above',
};

export const MERGE_WITH_BELOW_OPTION = {
	value: TREE_MENU_VALUES.mergeWithBelow,
	label: 'Merge with item below',
};

export const MERGE_MENU_OPTIONS = [
	MERGE_WITH_ABOVE_OPTION,
	MERGE_WITH_BELOW_OPTION,
];

export const getLeafNodesFromTree = (nodes, result = []) => {
	if (!nodes) return [];

	for (let i = 0, length = nodes.length; i < length; i++) {
		if (!nodes[i].children || nodes[i].children.length === 0) {
			result.push(nodes[i]);
		} else {
			result = getLeafNodesFromTree(nodes[i].children, result);
		}
	}

	return result;
};

export const getCompanySpendItemsFromTree = (nodes, company, result = []) => {
	for (let i = 0, length = nodes.length; i < length; i++) {
		if (
			(!nodes[i].children || nodes[i].children.length === 0) &&
			(nodes[i].level === 4 || nodes[i].level === 5) &&
			nodes[i].companyName === company
		) {
			result.push(nodes[i]);
		} else {
			result = getCompanySpendItemsFromTree(nodes[i].children, company, result);
		}
	}

	return result;
};

export const getAllSpendFromTree = from => {
	let value = 0;

	iterateOverTree(from, item => {
		if ((item.level === 4 || item.level === 5) && item.children.length === 0) {
			value += item.value;
		}
	});

	return value;
};

export const hasSpendItems = node => {
	if (node.children && node.children.length > 0) {
		return node.children.some(item => hasSpendItems(item));
	}

	return (
		(node.level === 4 || node.level === 5) &&
		(!node.children || node.children.length === 0)
	);
};

export const removeCompanySpendItemsFromTree = (nodes, company) => {
	const removedCompanySpendItems = getFilteredTree(nodes, node => {
		return !(
			(node.level === 4 || node.level === 5) &&
			(!node.children || node.children.length === 0) &&
			node.companyName === company
		);
	});

	const cleanTree = getFilteredTree(removedCompanySpendItems, node => {
		// remove those items that belong to the current company, and have no L4/L5's
		return !(node.companyName === company && !hasSpendItems(node));
	});

	return getNormalizedProjectTreeData(cleanTree);
};

export const getLeafIdsFromNode = node => {
	let ids = '';

	if (node.level === 4) {
		ids = node.id;
	} else {
		ids = [];

		iterateOverTree(node.children, item => {
			if (
				(item.level === 4 || item.level === 5) &&
				item.children.length === 0
			) {
				ids.push(item.id);
			}
		});
	}

	return ids;
};

const getExpandedStateMap = from => {
	const map = {};

	iterateOverTree(from, fromItem => {
		if (fromItem.expanded) {
			map[fromItem.id] = fromItem.expanded;
		}
	});

	return map;
};

export const getCompanyTreeData = ({
	companyOption,
	treeData,
	projectTreeData,
	showMappedItems,
	prevTreeData,
}) => {
	const expandedStateMap = prevTreeData
		? getExpandedStateMap(prevTreeData)
		: {};

	let companyTreeData = treeData.filter(
		item => item.name === companyOption.value
	);

	const color = companyOption.color;

	companyTreeData = mapOverTree(companyTreeData, item => {
		const expanded = item.level === 0 ? true : !!expandedStateMap[item.id];
		return {
			...item,
			companyColor: color,
			companyName: companyOption.value,
			expanded,
		};
	});

	if (projectTreeData) {
		companyTreeData = markMappedItems(companyTreeData, projectTreeData);

		if (!showMappedItems) {
			companyTreeData = removeMappedItems(companyTreeData);
		}
	}

	return companyTreeData;
};

export const getApTreeData = ({
	treeData,
	projectTreeData,
	showMappedItems,
	prevTreeData,
	color,
}) => {
	const expandedStateMap = prevTreeData
		? getExpandedStateMap(prevTreeData)
		: {};

	const topLevelItem = {
		name: 'AlixPartners taxonomy',
		id: 'AlixPartners taxonomy',
		originalId: 'AlixPartners taxonomy',
		level: -1,
		children: [...treeData],
		value: treeData.reduce((acc, item) => acc + item.value, 0),
	};

	let apTreeData = [topLevelItem];

	apTreeData = mapOverTree(apTreeData, item => {
		const level = item.level + 1;

		const expanded = level === 0 ? true : !!expandedStateMap[item.id];
		return {
			...item,
			level,
			expanded,
			companyColor: color,
		};
	});

	if (projectTreeData) {
		apTreeData = markMappedItems(apTreeData, projectTreeData);

		if (!showMappedItems) {
			apTreeData = removeMappedItems(apTreeData);
		}
	}

	return apTreeData;
};

export const mergeTreeNodes = (fromNode, toNode) => {
	fromNode = cloneDeep(fromNode);
	toNode = cloneDeep(toNode);

	if (fromNode.level === 4) {
		if (toNode.children.length === 0) {
			const toNodeCopy = cloneDeep(toNode);

			toNodeCopy.level = 5;
			toNodeCopy.children = [];
			delete toNodeCopy.chartData;

			toNode.id = toNode.id + '_virtual';
			toNode.children = [toNodeCopy];
		}

		if (fromNode.children.length === 0) {
			fromNode.level = 5;
			toNode.children = [fromNode, ...toNode.children];
		} else {
			toNode.children = [...fromNode.children, ...toNode.children];
		}
	} else {
		toNode.children = [...fromNode.children, ...toNode.children];
	}

	[toNode] = syncValues([toNode]);

	return toNode;
};

export const getApplyDataFromTree = from => {
	const to = mapOverTree(from, (item, _index, parent) => {
		return {
			...item,
			projectTaxonomy: parent ? [...parent.projectTaxonomy, item.name] : [],
		};
	});

	const result = [];
	iterateOverTree(to, item => {
		if ((item.level === 4 && item.children.length === 0) || item.level === 5) {
			result.push({
				id: item.id,
				projectTaxonomy: item.projectTaxonomy.slice(0, 4),
			});
		}
	});

	return result;
};

export const later = (delay, value) =>
	new Promise(resolve => setTimeout(resolve, delay, value));

export const syncValuesFromTreeData = (projectTreeData, treeData) => {
	// build value map from tree data - for every L4/L5
	const valueMap = {};
	iterateOverTree(treeData, item => {
		if ((item.level === 4 && item.children.length === 0) || item.level === 5) {
			valueMap[item.id] = item.value;
		}
	});
	// map over projectTreeData and update its value
	let newProjectTreeData = mapOverTree(projectTreeData, item => {
		if (valueMap[item.id] !== undefined) {
			return { ...item, value: valueMap[item.id] };
		}
		return { ...item };
	});
	// then update values for L1-3 nodes
	newProjectTreeData = syncValues(newProjectTreeData);

	return newProjectTreeData;
};

export const hasTreeChanges = (fromTree, toTree) => {
	return !isEqualWith(fromTree, toTree, (_value1, _value2, key) => {
		return key === 'expanded' ? true : undefined;
	});
};

const CONFIRMATION_DIALOG_TEXT = {
	removeLevelsText: (
		<React.Fragment>
			<div>
				You are about to move an item (including all its child items) to a lower
				level of the taxonomy.
			</div>
			<div>
				As the taxonomy is limited to 4 levels of hierarchy, we will remove the
				intermediate level categories (no impact to spend).
			</div>
		</React.Fragment>
	),

	addLevelsText: (
		<React.Fragment>
			<div>
				You are about to move an item (including all its child items) to a
				higher level of the taxonomy.
			</div>
			<div>
				As we require 4 levels of hierarchy, we will duplicate category labels
				to create the intermediate levels (no impact to overall spend).
			</div>
		</React.Fragment>
	),
};

export const getConfirmationDialogMessage = changeData => {
	let { insertedNodeLevel, parentNodeLevel, insertedNode } = changeData;
	const { addLevelsText, removeLevelsText } = CONFIRMATION_DIALOG_TEXT;

	insertedNodeLevel = insertedNodeLevel || 1; // L0 is a list of L1

	// insertedNodeLevel: 1 2 3 4 5
	// parentNodeLevel: 0 1 2 3 4

	const messagesMap = {
		'L2 becomes child of L0': addLevelsText,
		'L3 becomes child of L0': addLevelsText,
		'L4 becomes child of L0': addLevelsText,
		'L5 becomes child of L0': addLevelsText,

		'L1 becomes child of L1': removeLevelsText,
		'L3 becomes child of L1': addLevelsText,
		'L4 becomes child of L1': addLevelsText,
		'L5 becomes child of L1': addLevelsText,

		'L1 becomes child of L2': removeLevelsText,
		'L2 becomes child of L2': removeLevelsText,
		'L4 becomes child of L2': addLevelsText,
		'L5 becomes child of L2': addLevelsText,

		'L1 becomes child of L3': removeLevelsText,
		'L2 becomes child of L3': removeLevelsText,
		'L3 becomes child of L3': removeLevelsText,

		'L1 becomes child of L4': removeLevelsText,
		'L2 becomes child of L4': removeLevelsText,
		'L3 becomes child of L4': removeLevelsText,
	};

	let message =
		messagesMap[`L${insertedNodeLevel} becomes child of L${parentNodeLevel}`];

	if (message) {
		let ids = [];
		(Array.isArray(insertedNode) ? insertedNode : [insertedNode]).forEach(
			insertedNode => {
				const nodeIds = getLeafIdsFromNode(insertedNode);

				ids = [...ids, ...nodeIds];
			}
		);

		if (ids.length === 0) {
			message = '';
		}
	}

	return message;
};

export const getAllParentsInfo = (element, id) => {
	const result = [];

	const find = list => {
		const item = list.find(item => item.id === id || find(item.children));

		if (item) {
			result.push({
				level: item.level,
				name: item.name,
			});

			return true;
		}

		return false;
	};

	find(element.children);

	return result;
};

export const searchTree = (element, id) => {
	if (element.id === id) {
		return element;
	}

	if (element.children != null) {
		var i;
		var result = null;
		for (i = 0; result == null && i < element.children.length; i++) {
			result = searchTree(element.children[i], id);
		}
		return result;
	}

	return null;
};

export const applyAnonomizationTreeData = (
	treeData,
	companyAnonymizedMappingPairs,
	isDemoMode
) => {
	const nameMap = {};
	const allreadyAnonimizedNameMap = {};
	const reversedNameMap = {};
	const allreadyReversedNameMap = {};

	companyAnonymizedMappingPairs.forEach(({ companyName, anonymizedName }) => {
		nameMap[companyName] = anonymizedName;
		allreadyAnonimizedNameMap[anonymizedName] = anonymizedName;
		reversedNameMap[anonymizedName] = companyName;
		allreadyReversedNameMap[companyName] = companyName;
	});

	// map over projectTreeData and update its value
	let newProjectTreeData = mapOverTree(treeData, item => {
		const resItem = {
			...item,
			chartData:
				item.chartData &&
				item.chartData.map(chartDataItem => {
					return {
						...chartDataItem,
						name: isDemoMode
							? nameMap[chartDataItem.name] ||
							  allreadyAnonimizedNameMap[chartDataItem.name]
							: reversedNameMap[chartDataItem.name] ||
							  allreadyReversedNameMap[chartDataItem.name],
					};
				}),
		};

		if (item.companyName) {
			resItem.companyName = isDemoMode
				? nameMap[item.companyName] ||
				  allreadyAnonimizedNameMap[item.companyName]
				: reversedNameMap[item.companyName] ||
				  allreadyReversedNameMap[item.companyName];
		}

		return resItem;
	});

	return newProjectTreeData;
};
